import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Circle, CheckCircle, PlayCircle, DownloadSimple, RocketLaunch } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { DASHBOARD } from '@/constants/testIds';

const HowToUse = () => {
  const navigate = useNavigate();
  
  const steps = [
    {
      number: 1,
      title: 'Connect Origin AI',
      description: 'Start by connecting an Origin AI in the left panel. This AI communicates with you, understands your intent, and prepares instructions.',
      icon: <Circle size={24} className="text-[#00E5FF]" weight="fill" />
    },
    {
      number: 2,
      title: 'Write and Approve Instructions',
      description: 'Describe your objective to the Origin AI. It will structure your instructions clearly and ask for your approval before sending.',
      icon: <CheckCircle size={24} className="text-[#10B981]" weight="fill" />
    },
    {
      number: 3,
      title: 'Connect Production AI',
      description: 'Connect a Production AI in the right panel. This AI has access to development tools and performs the actual work.',
      icon: <Circle size={24} className="text-[#D97706]" weight="fill" />
    },
    {
      number: 4,
      title: 'Monitor the Interoperability Workspace',
      description: 'The center workspace shows messages flowing between AIs, questions from Production AI, and the active product being created.',
      icon: <ArrowRight size={24} className="text-[#00E5FF]" />
    },
    {
      number: 5,
      title: 'Review the Generated Result',
      description: 'Inspect the product preview, files created, and any technical details. Request corrections if needed.',
      icon: <PlayCircle size={24} className="text-[#00E5FF]" weight="fill" />
    },
    {
      number: 6,
      title: 'Run Pre-Deployment Sandbox',
      description: 'Test the result in a controlled sandbox environment before production. Review test results and approve when ready.',
      icon: <PlayCircle size={24} className="text-[#10B981]" weight="fill" />
    },
    {
      number: 7,
      title: 'Replace Production AI (Optional)',
      description: 'For multi-phase projects, disconnect one Production AI and connect another. The system generates a Handoff Package automatically.',
      icon: <ArrowRight size={24} className="text-[#D97706]" />
    },
    {
      number: 8,
      title: 'Archive or Deploy',
      description: 'Once approved, either archive the result for safekeeping or send it directly to production.',
      icon: <RocketLaunch size={24} className="text-[#10B981]" weight="fill" />
    }
  ];
  
  const faqs = [
    {
      q: 'What is AI Bridge Hub?',
      a: 'AI Bridge Hub is a neutral interoperability workspace where you connect, supervise, and coordinate different AI systems without being locked into a single provider.'
    },
    {
      q: 'Do I need API keys?',
      a: 'The demo mode works without any API keys. For production use, you would configure your preferred AI providers.'
    },
    {
      q: 'Can I use different AIs for different tasks?',
      a: 'Yes! You can replace the Production AI between project phases. The system creates a Handoff Package to transition smoothly.'
    },
    {
      q: 'Is my data shared between providers?',
      a: 'No. AI Bridge Hub only shares information you explicitly approve. You control what each AI can access.'
    },
    {
      q: 'What happens to my session data?',
      a: 'Sessions are temporary. You can archive or export results, but the platform does not permanently store AI conversations.'
    }
  ];
  
  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-[#0A0C10]">
      {/* Hero Section */}
      <div 
        className="relative h-[40vh] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1683447551794-1c287cd42675?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxhaSUyMG5ldHdvcmslMjBub2RlcyUyMGRhcmt8ZW58MHx8fHwxNzg0NTI4MTc0fDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0C10]/80 via-[#0A0C10]/90 to-[#0A0C10]"></div>
        <div className="relative z-10 text-center px-6">
          <h1 className="text-4xl lg:text-5xl font-bold terminal-font text-[#F3F4F6] mb-4 tracking-tight">
            HOW TO USE AI BRIDGE HUB
          </h1>
          <p className="text-lg body-font text-[#9CA3AF] max-w-2xl mx-auto leading-relaxed">
            Connect the AI that understands your intent with the AI that can produce the result.
          </p>
          <div className="mt-6 flex items-center justify-center gap-4">
            <Button 
              onClick={() => navigate('/')}
              className="bg-[#00E5FF] text-[#0A0C10] hover:bg-[#00E5FF]/90 terminal-font font-bold"
            >
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
      
      {/* Three-Column Layout Explanation */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="text-2xl font-bold terminal-font text-[#F3F4F6] mb-8 text-center">THE THREE-COLUMN WORKFLOW</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-[#13131A] border border-[#1F2937] p-6">
            <div className="text-[#00E5FF] terminal-font text-sm uppercase tracking-[0.2em] mb-3">Left Column (20%)</div>
            <h3 className="text-lg font-bold terminal-font text-[#F3F4F6] mb-3">Origin AI + Human</h3>
            <p className="text-sm body-font text-[#9CA3AF] leading-relaxed mb-4">
              Communicate with the Origin AI to clarify your objective. Prepare and approve instructions before they are sent.
            </p>
            <div className="text-xs terminal-font text-[#9CA3AF] space-y-1">
              <div>• Conversation history</div>
              <div>• Instruction preparation</div>
              <div>• Human approval controls</div>
            </div>
          </div>
          
          <div className="bg-[#13131A] border border-[#00E5FF] p-6">
            <div className="text-[#00E5FF] terminal-font text-sm uppercase tracking-[0.2em] mb-3">Center Workspace (60%)</div>
            <h3 className="text-lg font-bold terminal-font text-[#F3F4F6] mb-3">Interoperability Hub</h3>
            <p className="text-sm body-font text-[#9CA3AF] leading-relaxed mb-4">
              The main observation area. Watch messages flow, review the active product, run sandbox tests, and approve results.
            </p>
            <div className="text-xs terminal-font text-[#9CA3AF] space-y-1">
              <div>• Bidirectional messages</div>
              <div>• Active product viewer</div>
              <div>• Pre-deployment sandbox</div>
              <div>• Human decision point</div>
            </div>
          </div>
          
          <div className="bg-[#13131A] border border-[#1F2937] p-6">
            <div className="text-[#D97706] terminal-font text-sm uppercase tracking-[0.2em] mb-3">Right Column (20%)</div>
            <h3 className="text-lg font-bold terminal-font text-[#F3F4F6] mb-3">Production AI</h3>
            <p className="text-sm body-font text-[#9CA3AF] leading-relaxed mb-4">
              A replaceable production port. Connect, disconnect, or replace the Production AI at any phase of your project.
            </p>
            <div className="text-xs terminal-font text-[#9CA3AF] space-y-1">
              <div>• Temporary connection</div>
              <div>• Operation logs</div>
              <div>• Replaceable between phases</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Step-by-Step Guide */}
      <div className="max-w-4xl mx-auto px-6 py-16 border-t border-[#1F2937]">
        <h2 className="text-2xl font-bold terminal-font text-[#F3F4F6] mb-8 text-center">STEP-BY-STEP GUIDE</h2>
        <div className="space-y-6">
          {steps.map((step) => (
            <div key={step.number} className="flex gap-4 p-6 bg-[#13131A] border border-[#1F2937]">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#0A0C10] border border-[#1F2937] flex items-center justify-center terminal-font text-[#00E5FF] font-bold">
                  {step.number}
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg terminal-font text-[#F3F4F6] font-bold">{step.title}</h3>
                  {step.icon}
                </div>
                <p className="text-sm body-font text-[#9CA3AF] leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-6 py-16 border-t border-[#1F2937]">
        <h2 className="text-2xl font-bold terminal-font text-[#F3F4F6] mb-8 text-center">FREQUENTLY ASKED QUESTIONS</h2>
        <div className="space-y-4">
          {faqs.map((faq) => (
            <div key={faq.q} className="bg-[#13131A] border border-[#1F2937] p-6">
              <h3 className="text-base terminal-font text-[#00E5FF] mb-2 font-bold">{faq.q}</h3>
              <p className="text-sm body-font text-[#9CA3AF] leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* CTA Section */}
      <div className="max-w-4xl mx-auto px-6 py-16 border-t border-[#1F2937] text-center">
        <h2 className="text-2xl font-bold terminal-font text-[#F3F4F6] mb-4">READY TO BEGIN?</h2>
        <p className="text-base body-font text-[#9CA3AF] mb-8 max-w-2xl mx-auto leading-relaxed">
          Return to the dashboard and explore the preconfigured demonstration: "Build a Task Management Dashboard"
        </p>
        <div className="flex items-center justify-center gap-4">
          <Button 
            data-testid={DASHBOARD.guidedTourStart}
            onClick={() => navigate('/')}
            className="bg-[#00E5FF] text-[#0A0C10] hover:bg-[#00E5FF]/90 terminal-font font-bold px-8"
          >
            <PlayCircle size={16} className="mr-2" weight="fill" />
            Start Guided Demo
          </Button>
          <Button 
            onClick={() => navigate('/')}
            className="bg-[#13131A] border border-[#1F2937] text-[#F3F4F6] hover:bg-[#1C1C24] terminal-font"
          >
            Return to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HowToUse;